/**
 * Payment return page (/payment/confirming).
 *
 * The browser lands here after the payment gateway. Activation is NOT performed
 * here — it happens server-side on a verified webhook. This page only polls the
 * status and, once activated, tells the client to check their email to set a
 * password and sends them to sign in.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  HourglassBottomRounded,
  ErrorOutlineRounded,
} from '@mui/icons-material';
import PublicShell from './PublicShell';
import { goToSignIn } from './signIn';
import { pollPaymentActivated } from './onboardingApi';

/** Poll cadence and ceiling: ~90s total before falling back to the "processing" message. */
const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 30;

type Status = 'confirming' | 'success' | 'timeout' | 'missing_session';

export const PaymentConfirmingPage: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState<Status>(sessionId ? 'confirming' : 'missing_session');
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const activated = await pollPaymentActivated(sessionId);
        if (cancelled) return;
        if (activated) {
          setStatus('success');
          return;
        }
      } catch {
        // Transient errors are swallowed; keep polling until the ceiling.
      }
      if (cancelled) return;
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <PublicShell>
      {status === 'confirming' && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" fontWeight={700}>
            {translate('onboarding.payment.confirming_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.payment.confirming_body')}
          </Typography>
        </Stack>
      )}

      {status === 'success' && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <CheckCircleRounded sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6" fontWeight={700}>
            {translate('onboarding.payment.success_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.payment.success_body')}
          </Typography>
          <Button variant="contained" onClick={goToSignIn} sx={{ mt: 1 }}>
            {translate('onboarding.payment.go_to_sign_in')}
          </Button>
        </Stack>
      )}

      {status === 'timeout' && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <HourglassBottomRounded sx={{ fontSize: 56, color: 'warning.main' }} />
          <Typography variant="h6" fontWeight={700}>
            {translate('onboarding.payment.timeout_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.payment.timeout_body')}
          </Typography>
          <Button variant="outlined" onClick={goToSignIn} sx={{ mt: 1 }}>
            {translate('onboarding.payment.go_to_sign_in')}
          </Button>
        </Stack>
      )}

      {status === 'missing_session' && (
        <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: 'center' }}>
          <ErrorOutlineRounded sx={{ fontSize: 56, color: 'error.main' }} />
          <Typography variant="h6" fontWeight={700}>
            {translate('onboarding.payment.missing_session_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('onboarding.payment.missing_session_body')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/signup')} sx={{ mt: 1 }}>
            {translate('onboarding.payment.back_to_signup')}
          </Button>
        </Stack>
      )}
    </PublicShell>
  );
};

export default PaymentConfirmingPage;
